import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  User, Mail, Phone, Building2, MapPin, FileText, Briefcase, 
  CheckSquare, Bell, Ticket, MessageSquare, DollarSign, Printer,
  Plus, Trash2, Circle, CheckCircle, Clock, Edit, Save, X,
  Home, CreditCard, AlertCircle, ChevronRight, Send, Moon, Sun, LogOut,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'overview', label: 'Overview', icon: Home },
  { id: 'profile', label: 'My Profile', icon: User },
  { id: 'estimates', label: 'My Quotes', icon: FileText },
  { id: 'invoices', label: 'Invoices', icon: DollarSign },
  { id: 'projects', label: 'Projects', icon: Briefcase },
  { id: 'tasks', label: 'Tasks', icon: CheckSquare },
  { id: 'reminders', label: 'Reminders', icon: Bell },
  { id: 'tickets', label: 'Support', icon: Ticket },
  { id: 'notes', label: 'Notes', icon: MessageSquare },
  { id: 'financing', label: 'Submit to Financing', icon: CreditCard },
];

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
};

const STATUS_COLORS = {
  draft: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  sent: 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300',
  paid: 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300',
  overdue: 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300',
  open: 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300',
  in_progress: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300',
  resolved: 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300',
  closed: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
};

const CustomerPortal = () => {
  const { token, logout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [profile, setProfile] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [notes, setNotes] = useState([]);
  
  // Edit states
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editProfile, setEditProfile] = useState({});
  
  // Modal states
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectComment, setProjectComment] = useState('');
  
  // Form states
  const [taskForm, setTaskForm] = useState({ title: '', description: '', due_date: '', priority: 'medium' });
  const [reminderForm, setReminderForm] = useState({ title: '', description: '', remind_at: '', reminder_type: 'general' });
  const [ticketForm, setTicketForm] = useState({ subject: '', description: '', priority: 'medium', category: 'general' });
  const [noteContent, setNoteContent] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [summaryRes, profileRes] = await Promise.all([
        fetch(`${API_URL}/api/portal/dashboard-summary`, { headers }),
        fetch(`${API_URL}/api/portal/my-profile`, { headers })
      ]);
      
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        setProfile(profileData);
        setEditProfile(profileData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchTabData = useCallback(async (tab) => {
    const headers = { 'Authorization': `Bearer ${token}` };
    try {
      switch (tab) {
        case 'estimates':
          const estRes = await fetch(`${API_URL}/api/portal/my-estimates`, { headers });
          if (estRes.ok) setEstimates(await estRes.json());
          break;
        case 'invoices':
          const invRes = await fetch(`${API_URL}/api/portal/my-invoices`, { headers });
          if (invRes.ok) setInvoices(await invRes.json());
          break;
        case 'projects':
          const projRes = await fetch(`${API_URL}/api/portal/my-projects`, { headers });
          if (projRes.ok) setProjects(await projRes.json());
          break;
        case 'tasks':
          const taskRes = await fetch(`${API_URL}/api/portal/my-tasks`, { headers });
          if (taskRes.ok) setTasks(await taskRes.json());
          break;
        case 'reminders':
          const remRes = await fetch(`${API_URL}/api/portal/my-reminders`, { headers });
          if (remRes.ok) setReminders(await remRes.json());
          break;
        case 'tickets':
          const tickRes = await fetch(`${API_URL}/api/portal/my-tickets`, { headers });
          if (tickRes.ok) setTickets(await tickRes.json());
          break;
        case 'notes':
          const noteRes = await fetch(`${API_URL}/api/portal/my-notes`, { headers });
          if (noteRes.ok) setNotes(await noteRes.json());
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Error fetching ${tab}:`, error);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab !== 'overview' && activeTab !== 'profile') {
      fetchTabData(activeTab);
    }
  }, [activeTab, fetchTabData]);

  // Profile handlers
  const handleSaveProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portal/my-profile`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: editProfile.first_name,
          last_name: editProfile.last_name,
          phone: editProfile.phone,
          company: editProfile.company,
          address: editProfile.address
        })
      });
      if (response.ok) {
        const updated = await response.json();
        setProfile(updated);
        setIsEditingProfile(false);
        toast.success('Profile updated successfully');
        fetchData();
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  // Task handlers
  const handleCreateTask = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portal/my-tasks`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(taskForm)
      });
      if (response.ok) {
        toast.success('Task created');
        setShowTaskModal(false);
        setTaskForm({ title: '', description: '', due_date: '', priority: 'medium' });
        fetchTabData('tasks');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleToggleTask = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await fetch(`${API_URL}/api/portal/my-tasks/${taskId}?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTabData('tasks');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await fetch(`${API_URL}/api/portal/my-tasks/${taskId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Task deleted');
      fetchTabData('tasks');
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  // Reminder handlers
  const handleCreateReminder = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portal/my-reminders`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(reminderForm)
      });
      if (response.ok) {
        toast.success('Reminder set');
        setShowReminderModal(false);
        setReminderForm({ title: '', description: '', remind_at: '', reminder_type: 'general' });
        fetchTabData('reminders');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to create reminder');
    }
  };

  const handleCompleteReminder = async (reminderId) => {
    try {
      await fetch(`${API_URL}/api/portal/my-reminders/${reminderId}/complete`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Reminder completed');
      fetchTabData('reminders');
    } catch (error) {
      toast.error('Failed to complete reminder');
    }
  };

  // Ticket handlers
  const handleCreateTicket = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portal/my-tickets`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(ticketForm)
      });
      if (response.ok) {
        toast.success('Ticket submitted');
        setShowTicketModal(false);
        setTicketForm({ subject: '', description: '', priority: 'medium', category: 'general' });
        fetchTabData('tickets');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  // Note handlers
  const handleCreateNote = async () => {
    if (!noteContent.trim()) return;
    try {
      const response = await fetch(`${API_URL}/api/portal/my-notes`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: noteContent })
      });
      if (response.ok) {
        toast.success('Note saved');
        setShowNoteModal(false);
        setNoteContent('');
        fetchTabData('notes');
      }
    } catch (error) {
      toast.error('Failed to save note');
    }
  };

  // Estimate handlers
  const handleDeleteEstimate = async (estimateId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    try {
      const response = await fetch(`${API_URL}/api/portal/my-estimates/${estimateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Quote deleted');
        fetchTabData('estimates');
      } else {
        toast.error('Failed to delete quote');
      }
    } catch (error) {
      toast.error('Failed to delete quote');
    }
  };

  // Project comment handler
  const handleAddProjectComment = async () => {
    if (!projectComment.trim() || !selectedProject) return;
    try {
      const response = await fetch(`${API_URL}/api/portal/my-projects/${selectedProject.id}/comments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment: projectComment })
      });
      if (response.ok) {
        toast.success('Comment added');
        setProjectComment('');
        fetchTabData('projects');
        // Refresh selected project
        const projRes = await fetch(`${API_URL}/api/portal/my-projects/${selectedProject.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (projRes.ok) setSelectedProject(await projRes.json());
      }
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  // Print invoice
  const handlePrintInvoice = (invoice) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice ${invoice.invoice_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            .header { text-align: center; margin-bottom: 40px; }
            .company { font-size: 24px; font-weight: bold; color: #C41E3A; }
            .invoice-title { font-size: 20px; margin-top: 10px; }
            .details { margin: 20px 0; }
            .details p { margin: 5px 0; }
            .amount { font-size: 28px; font-weight: bold; color: #C41E3A; margin: 30px 0; }
            .status { display: inline-block; padding: 5px 15px; border-radius: 20px; background: #22c55e; color: white; }
            .footer { margin-top: 40px; text-align: center; color: #666; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company">Fusion Builder Pro</div>
            <div class="invoice-title">INVOICE</div>
          </div>
          <div class="details">
            <p><strong>Invoice Number:</strong> ${invoice.invoice_number}</p>
            <p><strong>Title:</strong> ${invoice.title}</p>
            <p><strong>Date:</strong> ${new Date(invoice.created_at).toLocaleDateString()}</p>
            ${invoice.due_date ? `<p><strong>Due Date:</strong> ${new Date(invoice.due_date).toLocaleDateString()}</p>` : ''}
            <p><strong>Status:</strong> <span class="status">${invoice.status?.toUpperCase()}</span></p>
          </div>
          <div class="amount">Total: $${invoice.amount?.toLocaleString()}</div>
          ${invoice.notes ? `<div class="details"><p><strong>Notes:</strong> ${invoice.notes}</p></div>` : ''}
          <div class="footer">
            <p>Thank you for your business!</p>
            <p>Fusion Builder Pro | support@fusionbuilderpro.com</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C41E3A]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950 transition-colors">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-gray-900 dark:text-white hidden sm:block">
                Fusion<span className="text-orange-500">Builder</span>Pro
              </span>
            </Link>
            <div className="hidden sm:block">
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Customer Portal</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Welcome, {summary?.profile?.name || user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5 text-gray-600" />}
            </button>
            <Button variant="outline" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Navigation */}
          <aside className="lg:w-64 flex-shrink-0">
            <nav className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-2">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === tab.id
                      ? 'bg-[#C41E3A] text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                  data-testid={`portal-tab-${tab.id}`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 space-y-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && !summary && (
              <Card>
                <CardContent className="py-12 text-center">
                  <AlertCircle className="w-16 h-16 mx-auto mb-4 text-yellow-500" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    No Customer Profile Found
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-4">
                    Your account is not linked to a customer profile yet. Please contact support to set up your customer portal access.
                  </p>
                  <p className="text-sm text-gray-400">
                    Email: <a href="mailto:support@fusionbuilderpro.com" className="text-blue-600 hover:underline">support@fusionbuilderpro.com</a>
                    <br/>
                    Phone: <a href="tel:8338815529" className="text-[#C41E3A] hover:underline">(833) 881-5529</a>
                  </p>
                </CardContent>
              </Card>
            )}
            {activeTab === 'overview' && summary && (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-green-100">Paid Invoices</p>
                          <p className="text-3xl font-bold">${summary.invoices?.paid_amount?.toLocaleString() || 0}</p>
                        </div>
                        <DollarSign className="w-10 h-10 text-green-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-blue-100">Active Projects</p>
                          <p className="text-3xl font-bold">{summary.projects?.active || 0}</p>
                        </div>
                        <Briefcase className="w-10 h-10 text-blue-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-orange-100">Pending Tasks</p>
                          <p className="text-3xl font-bold">{summary.tasks?.pending || 0}</p>
                        </div>
                        <CheckSquare className="w-10 h-10 text-orange-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-purple-100">Open Tickets</p>
                          <p className="text-3xl font-bold">{summary.tickets?.open || 0}</p>
                        </div>
                        <Ticket className="w-10 h-10 text-purple-200" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Quick Actions */}
                <Card>
                  <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <Button variant="outline" className="h-auto py-4 flex-col" onClick={() => { setActiveTab('invoices'); }}>
                        <FileText className="w-6 h-6 mb-2" />
                        View Invoices
                      </Button>
                      <Button variant="outline" className="h-auto py-4 flex-col" onClick={() => setShowTaskModal(true)}>
                        <Plus className="w-6 h-6 mb-2" />
                        Add Task
                      </Button>
                      <Button variant="outline" className="h-auto py-4 flex-col" onClick={() => setShowTicketModal(true)}>
                        <Ticket className="w-6 h-6 mb-2" />
                        New Ticket
                      </Button>
                      <Button variant="outline" className="h-auto py-4 flex-col" onClick={() => setActiveTab('profile')}>
                        <User className="w-6 h-6 mb-2" />
                        Edit Profile
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Invoice Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>Invoice Summary</span>
                      <Button variant="outline" size="sm" onClick={() => setActiveTab('invoices')}>
                        View All <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{summary.invoices?.total || 0}</p>
                        <p className="text-sm text-gray-500">Total Invoices</p>
                      </div>
                      <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{summary.invoices?.paid || 0}</p>
                        <p className="text-sm text-gray-500">Paid</p>
                      </div>
                      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                        <p className="text-2xl font-bold text-yellow-600">{summary.invoices?.pending || 0}</p>
                        <p className="text-sm text-gray-500">Pending</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && profile && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>My Profile</CardTitle>
                  {isEditingProfile ? (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setIsEditingProfile(false); setEditProfile(profile); }}>
                        <X className="w-4 h-4 mr-1" /> Cancel
                      </Button>
                      <Button size="sm" onClick={handleSaveProfile}>
                        <Save className="w-4 h-4 mr-1" /> Save
                      </Button>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setIsEditingProfile(true)}>
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Contact Info */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Contact Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-500">First Name</label>
                        {isEditingProfile ? (
                          <Input value={editProfile.first_name || ''} onChange={(e) => setEditProfile({ ...editProfile, first_name: e.target.value })} />
                        ) : (
                          <p className="font-medium text-gray-900 dark:text-white">{profile.first_name}</p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Last Name</label>
                        {isEditingProfile ? (
                          <Input value={editProfile.last_name || ''} onChange={(e) => setEditProfile({ ...editProfile, last_name: e.target.value })} />
                        ) : (
                          <p className="font-medium text-gray-900 dark:text-white">{profile.last_name}</p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Email</label>
                        <p className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          {profile.email}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Phone</label>
                        {isEditingProfile ? (
                          <Input value={editProfile.phone || ''} onChange={(e) => setEditProfile({ ...editProfile, phone: e.target.value })} />
                        ) : (
                          <p className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            {profile.phone || 'Not provided'}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Company</label>
                        {isEditingProfile ? (
                          <Input value={editProfile.company || ''} onChange={(e) => setEditProfile({ ...editProfile, company: e.target.value })} />
                        ) : (
                          <p className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            {profile.company || 'Not provided'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Address</h3>
                    {isEditingProfile ? (
                      <div className="space-y-3">
                        <Input
                          placeholder="Street Address"
                          value={editProfile.address?.street || ''}
                          onChange={(e) => setEditProfile({ ...editProfile, address: { ...editProfile.address, street: e.target.value } })}
                        />
                        <div className="grid grid-cols-3 gap-3">
                          <Input
                            placeholder="City"
                            value={editProfile.address?.city || ''}
                            onChange={(e) => setEditProfile({ ...editProfile, address: { ...editProfile.address, city: e.target.value } })}
                          />
                          <Input
                            placeholder="State"
                            value={editProfile.address?.state || ''}
                            onChange={(e) => setEditProfile({ ...editProfile, address: { ...editProfile.address, state: e.target.value } })}
                          />
                          <Input
                            placeholder="ZIP"
                            value={editProfile.address?.zip_code || ''}
                            onChange={(e) => setEditProfile({ ...editProfile, address: { ...editProfile.address, zip_code: e.target.value } })}
                          />
                        </div>
                      </div>
                    ) : profile.address?.street ? (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{profile.address.street}</p>
                          <p className="text-gray-600 dark:text-gray-300">
                            {profile.address.city}, {profile.address.state} {profile.address.zip_code}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic">No address provided</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* My Quotes/Estimates Tab */}
            {activeTab === 'estimates' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>My Saved Quotes</CardTitle>
                  <Link to="/">
                    <Button size="sm">
                      <Plus className="w-4 h-4 mr-2" /> Create New Quote
                    </Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  {estimates.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="mb-4">No saved quotes yet</p>
                      <Link to="/">
                        <Button>
                          <Plus className="w-4 h-4 mr-2" /> Configure Your Building
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {estimates.map((estimate) => (
                        <div key={estimate.id} className="border dark:border-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 dark:text-white">{estimate.title}</p>
                              <p className="text-sm text-gray-500">{estimate.description}</p>
                              <div className="flex items-center gap-3 mt-2">
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                                  {estimate.stage || 'Draft'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  Created: {new Date(estimate.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              {estimate.configuration && (
                                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-500 dark:text-gray-400">
                                  <span>Style: {estimate.configuration.style}</span>
                                  <span>Size: {estimate.configuration.width}&apos; × {estimate.configuration.length}&apos;</span>
                                  <span>Height: {estimate.configuration.legHeight}&apos;</span>
                                  <span>Roof: {estimate.configuration.roofStyle}</span>
                                </div>
                              )}
                            </div>
                            <div className="text-right flex items-center gap-2 ml-4">
                              {estimate.value > 0 && (
                                <p className="text-xl font-bold text-[#C41E3A] mr-2">${estimate.value?.toLocaleString()}</p>
                              )}
                              <Link to={`/?edit=${estimate.id}`}>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                  data-testid={`edit-quote-${estimate.id}`}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                              </Link>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => handleDeleteEstimate(estimate.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Invoices Tab */}
            {activeTab === 'invoices' && (
              <Card>
                <CardHeader>
                  <CardTitle>My Invoices</CardTitle>
                </CardHeader>
                <CardContent>
                  {invoices.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No invoices yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {invoices.map((invoice) => (
                        <div key={invoice.id} className="border dark:border-gray-700 rounded-lg p-4 flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{invoice.title}</p>
                            <p className="text-sm text-gray-500">{invoice.invoice_number}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[invoice.status]}`}>
                                {invoice.status}
                              </span>
                              {invoice.due_date && (
                                <span className="text-xs text-gray-500">Due: {new Date(invoice.due_date).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex items-center gap-3">
                            <p className="text-xl font-bold text-gray-900 dark:text-white">${invoice.amount?.toLocaleString()}</p>
                            {invoice.status === 'paid' && (
                              <Button variant="outline" size="sm" onClick={() => handlePrintInvoice(invoice)} title="Print Invoice">
                                <Printer className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Projects Tab */}
            {activeTab === 'projects' && (
              <Card>
                <CardHeader>
                  <CardTitle>My Projects</CardTitle>
                </CardHeader>
                <CardContent>
                  {projects.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No projects yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {projects.map((project) => (
                        <div key={project.id} className="border dark:border-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{project.name}</p>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[project.status] || 'bg-gray-100 text-gray-700'}`}>
                                {project.status?.replace('_', ' ')}
                              </span>
                            </div>
                            <p className="text-xl font-bold text-gray-900 dark:text-white">${project.total_value?.toLocaleString()}</p>
                          </div>
                          
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full"
                            onClick={async () => {
                              const res = await fetch(`${API_URL}/api/portal/my-projects/${project.id}`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (res.ok) setSelectedProject(await res.json());
                            }}
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            View & Comment
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Tasks Tab */}
            {activeTab === 'tasks' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>My Tasks</CardTitle>
                  <Button size="sm" onClick={() => setShowTaskModal(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Add Task
                  </Button>
                </CardHeader>
                <CardContent>
                  {tasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <CheckSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No tasks yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {tasks.map((task) => (
                        <div key={task.id} className={`border dark:border-gray-700 rounded-lg p-4 ${task.status === 'completed' ? 'opacity-60' : ''}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <button onClick={() => handleToggleTask(task.id, task.status)}>
                                {task.status === 'completed' ? (
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                ) : (
                                  <Circle className="w-5 h-5 text-gray-400" />
                                )}
                              </button>
                              <div>
                                <p className={`font-medium text-gray-900 dark:text-white ${task.status === 'completed' ? 'line-through' : ''}`}>
                                  {task.title}
                                </p>
                                {task.description && <p className="text-sm text-gray-500">{task.description}</p>}
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS[task.priority]}`}>
                                    {task.priority}
                                  </span>
                                  {task.due_date && (
                                    <span className="text-xs text-gray-500">Due: {new Date(task.due_date).toLocaleDateString()}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteTask(task.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Reminders Tab */}
            {activeTab === 'reminders' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>My Reminders</CardTitle>
                  <Button size="sm" onClick={() => setShowReminderModal(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Set Reminder
                  </Button>
                </CardHeader>
                <CardContent>
                  {reminders.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No reminders set</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {reminders.map((reminder) => (
                        <div key={reminder.id} className={`border dark:border-gray-700 rounded-lg p-4 ${reminder.is_completed ? 'opacity-60' : ''}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <Bell className={`w-5 h-5 ${reminder.is_completed ? 'text-gray-400' : 'text-orange-500'}`} />
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">{reminder.title}</p>
                                {reminder.description && <p className="text-sm text-gray-500">{reminder.description}</p>}
                                <p className="text-xs text-orange-600 mt-1">
                                  <Clock className="w-3 h-3 inline mr-1" />
                                  {new Date(reminder.remind_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                            {!reminder.is_completed && (
                              <Button variant="outline" size="sm" onClick={() => handleCompleteReminder(reminder.id)}>
                                Complete
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Tickets Tab */}
            {activeTab === 'tickets' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Support Tickets</CardTitle>
                  <Button size="sm" onClick={() => setShowTicketModal(true)}>
                    <Plus className="w-4 h-4 mr-2" /> New Ticket
                  </Button>
                </CardHeader>
                <CardContent>
                  {tickets.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Ticket className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No tickets yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {tickets.map((ticket) => (
                        <div key={ticket.id} className="border dark:border-gray-700 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{ticket.subject}</p>
                              <p className="text-sm text-gray-500">{ticket.ticket_number}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[ticket.status]}`}>
                                  {ticket.status}
                                </span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS[ticket.priority]}`}>
                                  {ticket.priority}
                                </span>
                              </div>
                            </div>
                            <p className="text-xs text-gray-400">{new Date(ticket.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Notes Tab */}
            {activeTab === 'notes' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>My Notes</CardTitle>
                  <Button size="sm" onClick={() => setShowNoteModal(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Add Note
                  </Button>
                </CardHeader>
                <CardContent>
                  {notes.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No notes yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {notes.map((note) => (
                        <div key={note.id} className="border-l-4 border-blue-400 pl-4 py-2">
                          <p className="text-gray-800 dark:text-gray-100">{note.description}</p>
                          <p className="text-xs text-gray-400 mt-1">{new Date(note.created_at).toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Submit to Financing Tab */}
            {activeTab === 'financing' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-green-600" />
                    Submit to Financing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="py-6">
                    <div className="text-center mb-8">
                      <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CreditCard className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                        Flexible Financing Options
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                        Choose from our trusted financing partners to make your steel building project affordable.
                      </p>
                    </div>
                    
                    {/* Financing Partners */}
                    <div className="max-w-2xl mx-auto space-y-4">
                      {/* LightStream */}
                      <a 
                        href="https://www.lightstream.com/home-improvement-loan?cid=AA-14133&irad=88401&fact=14133&irmp=5045678&isredirect=True"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-5 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 transition-all group shadow-sm hover:shadow-md"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                            <DollarSign className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                              LightStream
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Home Improvement Loan</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                          <span className="text-sm font-medium">Apply Now</span>
                          <ExternalLink className="w-4 h-4" />
                        </div>
                      </a>

                      {/* HFS Financial */}
                      <a 
                        href="https://www.hfsfinancial.net/promo/681523ce00dae585eeb653fd"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-5 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-green-500 dark:hover:border-green-500 transition-all group shadow-sm hover:shadow-md"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400">
                              HFS Financial
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Financing Solutions</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                          <span className="text-sm font-medium">Apply Now</span>
                          <ExternalLink className="w-4 h-4" />
                        </div>
                      </a>

                      {/* RTO National - Lease */}
                      <a 
                        href="https://www.rtonational.com/checkout/contract/customer/new?link=bldconfigapp"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-5 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-purple-500 dark:hover:border-purple-500 transition-all group shadow-sm hover:shadow-md"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                            <FileText className="w-6 h-6 text-purple-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400">
                              Lease Application - RTO
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Rent-To-Own Lease Program</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400">
                          <span className="text-sm font-medium">Apply Now</span>
                          <ExternalLink className="w-4 h-4" />
                        </div>
                      </a>

                      {/* RTO National - Finance */}
                      <a 
                        href="https://www.rtonational.com/checkout/installment/customer/new?link=bldconfigapp"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-5 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-orange-500 dark:hover:border-orange-500 transition-all group shadow-sm hover:shadow-md"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                            <CreditCard className="w-6 h-6 text-orange-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400">
                              Finance Application - RTO
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Rent-To-Own Installment Plan</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
                          <span className="text-sm font-medium">Apply Now</span>
                          <ExternalLink className="w-4 h-4" />
                        </div>
                      </a>
                    </div>

                    <p className="text-sm text-gray-400 text-center mt-8">
                      Questions about financing? Contact us at{' '}
                      <a href="tel:8338815529" className="text-[#C41E3A] hover:underline">(833) 881-5529</a>
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </main>
        </div>
      </div>

      {/* Project Detail Modal */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedProject?.name}</DialogTitle>
          </DialogHeader>
          {selectedProject && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[selectedProject.status]}`}>
                  {selectedProject.status?.replace('_', ' ')}
                </span>
                <p className="text-xl font-bold">${selectedProject.total_value?.toLocaleString()}</p>
              </div>

              {/* Comments */}
              <div>
                <h4 className="font-medium mb-3">Comments</h4>
                <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
                  {selectedProject.comments?.length === 0 ? (
                    <p className="text-gray-500 text-sm">No comments yet</p>
                  ) : (
                    selectedProject.comments?.map((comment) => (
                      <div key={comment.id} className={`p-3 rounded-lg ${
                        comment.author_type === 'customer' ? 'bg-blue-50 dark:bg-blue-900/20 ml-4' : 'bg-gray-50 dark:bg-gray-800 mr-4'
                      }`}>
                        <p className="text-sm">{comment.comment}</p>
                        <p className="text-xs text-gray-400 mt-1">{comment.author_name} • {new Date(comment.created_at).toLocaleString()}</p>
                      </div>
                    ))
                  )}
                </div>

                {/* Add Comment */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a comment..."
                    value={projectComment}
                    onChange={(e) => setProjectComment(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddProjectComment()}
                  />
                  <Button onClick={handleAddProjectComment}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Task Modal */}
      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Title</label>
              <Input value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} placeholder="Task title" />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <textarea className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800" value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Due Date</label>
              <Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Priority</label>
              <select className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600" value={taskForm.priority} onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskModal(false)}>Cancel</Button>
            <Button onClick={handleCreateTask}>Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminder Modal */}
      <Dialog open={showReminderModal} onOpenChange={setShowReminderModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Reminder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Title</label>
              <Input value={reminderForm.title} onChange={(e) => setReminderForm({ ...reminderForm, title: e.target.value })} placeholder="Reminder title" />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <textarea className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600" value={reminderForm.description} onChange={(e) => setReminderForm({ ...reminderForm, description: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Remind At</label>
              <Input type="datetime-local" value={reminderForm.remind_at} onChange={(e) => setReminderForm({ ...reminderForm, remind_at: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReminderModal(false)}>Cancel</Button>
            <Button onClick={handleCreateReminder}>Set Reminder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Modal */}
      <Dialog open={showTicketModal} onOpenChange={setShowTicketModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Subject</label>
              <Input value={ticketForm.subject} onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })} placeholder="Brief description of your issue" />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <textarea className="w-full border rounded-md px-3 py-2 min-h-[100px] bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600" value={ticketForm.description} onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })} placeholder="Please describe your issue in detail..." />
            </div>
            <div>
              <label className="text-sm font-medium">Category</label>
              <select className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600" value={ticketForm.category} onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}>
                <option value="general">General</option>
                <option value="billing">Billing</option>
                <option value="technical">Technical</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Priority</label>
              <select className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600" value={ticketForm.priority} onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value })}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTicketModal(false)}>Cancel</Button>
            <Button onClick={handleCreateTicket}>Submit Ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Note Modal */}
      <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <textarea
              className="w-full border rounded-md px-3 py-2 min-h-[150px] bg-white dark:bg-gray-800"
              placeholder="Write your note..."
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoteModal(false)}>Cancel</Button>
            <Button onClick={handleCreateNote}>Save Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerPortal;
